import type { AxiosError } from 'axios';
import axios from 'axios';
import { unstable_getServerSession } from 'next-auth';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from '../components/elements/Button';
import Label from '../components/elements/Forms/Label';
import TextInput from '../components/elements/Forms/TextInput';
import Heading2 from '../components/elements/Heading/Heading2';
import Link from '../components/elements/Link';
import Paragraph from '../components/elements/Paragraph';
import BasicCardLayout from '../components/modules/Layout/BasicLayout/BasicCardLayout';
import Seo from '../components/modules/Seo';
import routes from '../config/routes';
import { googleSignIn } from '../features/auth';
import { signUp } from '../features/user';
import { ErrorMessages } from '../lib/errorMessages';
import type { PageContext, PageRedirect } from '../lib/interfaces/page';
import { goToDashboard, goToHomePage } from '../lib/page-helper';
import { authOptions } from './api/auth/[...nextauth].page';

type FromValues = {
    email: string;
    password: string;
};

interface HandleRegisterProps {
    email: string;
    password: string;
    router: NextRouter;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    setIsError: Dispatch<SetStateAction<boolean>>;
    setErrorMessage: Dispatch<SetStateAction<string>>;
}

async function handleRegister({
    email,
    password,
    router,
    setIsLoading,
    setIsError,
    setErrorMessage,
}: HandleRegisterProps): Promise<void> {
    setIsError(false);
    setIsLoading(true);
    try {
        await signUp(email, password);

        await router.push({
            pathname: routes.checkEmail.getUrl(),
            query: { email },
        });
    } catch (error) {
        setIsError(true);
        if (axios.isAxiosError(error)) {
            if (
                (error as AxiosError<{ error: { message: string } }>).response?.data?.error
                    ?.message === 'Email or Username are already taken'
            ) {
                setErrorMessage(
                    "Impossible de créer le compte. Vérifie que vous n'êtes pas déjà enregistré avec cet email",
                );
                return;
            }
        }
        setErrorMessage(ErrorMessages.INTERNAL_ERROR);
        console.error(error);
    } finally {
        setIsLoading(false);
    }
}

export interface RegisterProps {
    prefilledEmail?: string;
}

export default function Register({ prefilledEmail }: RegisterProps): JSX.Element {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { register, handleSubmit } = useForm<FromValues>({
        defaultValues: { email: prefilledEmail || '', password: '' },
    });

    function onSubmit({ email, password }: FromValues): void {
        handleRegister({
            email,
            password,
            router,
            setIsLoading,
            setIsError,
            setErrorMessage,
        });
    }

    return (
        <BasicCardLayout>
            <Seo
                title="Créer un compte"
                description="Inscrivez-vous sur Partager et retrouvez vos auteurs et publications préférées."
                url={routes.register.getUrl()}
                isCanonical
            />
            <Heading2 className="!mt-0" level={1}>
                S’inscrire
            </Heading2>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-6">
                    <Label htmlFor="login-email">Adresse e-mail</Label>
                    <TextInput
                        id="login-email"
                        type="email"
                        className="w-full"
                        name="email"
                        {...(prefilledEmail && { disabled: true })}
                        register={register}
                    />
                </div>
                <div className="mb-6">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <TextInput
                        id="login-password"
                        type="password"
                        className="w-full"
                        name="password"
                        register={register}
                    />
                </div>
                <Button type="submit" className="!w-full mb-6" loading={isLoading}>
                    Suivant
                </Button>
                {isError && (
                    <Paragraph variant="error" className="text-center">
                        {errorMessage}
                    </Paragraph>
                )}
            </form>
            <Paragraph className="text-center mb-6">Ou</Paragraph>
            <Button className="!w-full mb-6" color="alternative" onClick={() => googleSignIn()}>
                <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block  mr-5">
                    <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                        <path
                            fill="#4285F4"
                            d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"
                        />
                        <path
                            fill="#34A853"
                            d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"
                        />
                        <path
                            fill="#EA4335"
                            d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"
                        />
                    </g>
                </svg>
                Se connecter avec Google
            </Button>
            <Paragraph className="text-center">
                Vous avez un compte? <Link href={routes.login.getUrl()}>Connectez-vous</Link>
            </Paragraph>
        </BasicCardLayout>
    );
}

export async function getServerSideProps(
    context: PageContext,
): Promise<{ props: RegisterProps } | PageRedirect> {
    const session = await unstable_getServerSession(context.req, context.res, authOptions);
    if (session) {
        return goToDashboard();
    }
    const { prefilledEmail } = context.query;

    if (!prefilledEmail) {
        return { props: {} };
    }

    if (typeof prefilledEmail !== 'string') {
        return goToHomePage(ErrorMessages.MALFORMED_URL);
    }

    const emailDecode = Buffer.from(prefilledEmail, 'base64').toString('ascii');

    return {
        props: {
            prefilledEmail: emailDecode,
        },
    };
}
